import { defineComponent as _defineComponent } from 'vue'
import { openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, unref as _unref, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, createVNode as _createVNode, createElementBlock as _createElementBlock, Teleport as _Teleport, normalizeClass as _normalizeClass } from "vue"

const _hoisted_1 = { class: "content-layout" }
const _hoisted_2 = { class: "main-content-wrapper" }
const _hoisted_3 = {
  key: 3,
  class: "related-games-inline-wrapper"
}
const _hoisted_4 = { class: "related-games-header" }
const _hoisted_5 = { class: "related-games-title" }
const _hoisted_6 = {
  key: 8,
  class: "mobile-back-container"
}
const _hoisted_7 = { class: "mobile-back-text" }
const _hoisted_8 = {
  key: 0,
  class: "betslip-wrapper"
}
const _hoisted_9 = {
  key: 0,
  class: "mobile-view-bets-container"
}

import { ref, onMounted, onUnmounted, watch, computed } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useI18n } from "vue-i18n";
import { useBetslipStore } from "../stores/betslip";
import { useGameStore } from "../stores/game";
import { useAuthStore } from "../stores/auth";
import { useToast } from "../composables/useToast";
import { useModalStore } from "../stores/modal";
import type { BetslipItem } from "../stores/betslip";
import BetSlip from "../components/BetSlip.vue";
import MobileDrawsSection from "../components/mobile/MobileDrawsSection.vue";
import DesktopDrawsSection from "../components/desktop/DesktopDrawsSection.vue";
import MobileBettingSection from "../components/mobile/MobileBettingSection.vue";
import MobileConfirmSection from "../components/mobile/MobileConfirmSection.vue";
import MobileHeader from "../components/mobile/MobileHeader.vue";
import DesktopHeader from "../components/desktop/DesktopHeader.vue";
import DesktopBettingSection from "../components/desktop/DesktopBettingSection.vue";
import DesktopConfirmSection from "../components/desktop/DesktopConfirmSection.vue";
import RelatedGamesSelector from "../components/RelatedGamesSelector.vue";
import VideoLiveStream from "../components/VideoLiveStream.vue";
import { canShowLiveStream } from "../stores/stream";
import { useRelatedGames } from "../composables/useRelatedGames";
import { useRelatedGameMatching } from "../composables/useRelatedGameMatching";
import { iframeCommunication, DETAIL_MOBILE_VIEWPORT_CLASS } from "../utils/iframeCommunication";
import { resolveMarketOutcome, normalizeSpecifier } from "@/constants/sameColorBalls";
import { isStandardBetPlusColorBonusMarket } from "@/constants/standardBetPlusColorBonus";
import { useIntegrationMode } from '@/composables/useIntegrationMode';
import { buildSelectedDrawsArray, parseDrawQuery } from '@/utils/deepLink';

interface Game {
  id: string;
  name: string;
  slug: string;
  flagUrl: string;
  drawTime: Date;
  cutoffTime: Date;
  country: string;
  draw_date?: string;
  cutoff?: string;
  game_name?: string;
  game_slug?: string;
  game_logo?: string;
}

interface MarketOutcome {
  id: number;
  game_market_id?: number;
  text: string;
  select_numbers: boolean;
  pick: number;
  slug: string;
  odds: string;
  specifier: string | null;
}

interface Market {
  id: number;
  name: string;
  slug: string;
  description: string;
  ordering: number;
  outcomes: MarketOutcome[];
}

interface SelectedMarket {
  marketGroup: MarketGroup;
  market: Market;
  label: string;
  odds: number;
  select_numbers: boolean;
  balls: number;
  outcome_id: string;
  specifier?: string;
  game_market_id?: number;
}

interface MarketGroup {
  id: number;
  name: string;
  slug: string;
  ordering: number;
  has_bonus: boolean;
  markets: Market[];
  rules: Array<{
    id: number;
    name: string;
    balls_min: number;
    balls_max: number;
    picks_min: number;
    picks_max: number;
    has_bonus: number | boolean;
    max_resulted: number;
    type: string;
  }>;
}

interface Line {
  numbers: number[];
  bonus?: number;
  bonusColor?: import('@/constants/sameColorBalls').BallColorSlug;
  numBets: number;
  picks: number;
  market?: SelectedMarket;
}

const DETAIL_STICKY_HEADER_CLASS = 'detail-sticky-header';


export default /*@__PURE__*/_defineComponent({
  __name: 'DetailView',
  emits: ['openBetslipModal', 'drawsChanged'],
  setup(__props, { expose: __expose, emit: __emit }) {

const route = useRoute();
const router = useRouter();
const gameStore = useGameStore();
const authStore = useAuthStore();
const { showToast } = useToast();
const { t } = useI18n();
const modalStore = useModalStore();
const { isLite } = useIntegrationMode();
const emit = __emit;

const useOnlinePlatformBetslip = computed(() => {
  const ch = gameStore.getBetSettings?.client_surface_ui?.online;
  return ch?.use_platform_betslip !== false;
});

const onlineUiSettings = computed(() => gameStore.getBetSettings?.client_surface_ui?.online);
const streamlinedNumberSelection = computed(
  () => onlineUiSettings.value?.streamlined_number_selection === true
);
const hotColdNumbersEnabled = computed(
  () => !isLite.value && onlineUiSettings.value?.enable_hot_cold_numbers === true
);

const lottery = ref<Game | null>(null);
const showStream = ref(false);
const selectedDrawsArray = ref<boolean[]>([]);

const applyDrawSelectionFromQuery = () => {
  const nextDraws = gameStore.getValidDraws || [];
  const drawId = parseDrawQuery(route.query.draw);
  selectedDrawsArray.value = buildSelectedDrawsArray(nextDraws, drawId);
};
const selectedMarket = ref<SelectedMarket | null>(null);
const mobileActiveTab = ref<'main' | 'bonus'>('main');
const isMobile = ref(window.innerWidth <= 768);
const lines = ref<Line[]>([]);
const bettingMarkets = ref<MarketGroup[]>([]);
const syncMobileViewportLayout = () => {
  const html = document.documentElement;
  // Full-page scroll like list views — do not lock a middle scrollport.
  document.body.classList.remove(DETAIL_MOBILE_VIEWPORT_CLASS);
  html.style.removeProperty('--detail-mobile-viewport-height');

  // Allow sticky mobile header (overflow-x:hidden on #app otherwise blocks it).
  document.body.classList.toggle(DETAIL_STICKY_HEADER_CLASS, isMobile.value);

  if (isMobile.value && useOnlinePlatformBetslip.value) {
    html.style.setProperty('--detail-mobile-fixed-footer-height', '72px');
  } else {
    html.style.removeProperty('--detail-mobile-fixed-footer-height');
  }

  if (iframeCommunication.isRunningInIframe()) {
    iframeCommunication.sendImmediateHeightUpdate();
  }
};

const syncBettingMarketsFromStore = () => {
  const marketGroups = gameStore.getMarketGroups || [];
  bettingMarkets.value = marketGroups.map(group => ({
    id: group.id,
    name: group.name,
    slug: group.slug,
    ordering: group.ordering,
    has_bonus: group.has_bonus,
    rules: group.rules,
    markets: group.markets.map(market => ({
      id: market.id,
      name: market.name,
      slug: market.slug,
      description: market.description,
      ordering: market.ordering,
      outcomes: market.outcomes || []
    }))
  }));
};

const isClearingAfterSubmission = ref(false);
const lastAddedBet = ref<BetslipItem | null>(null);
const selectedRelatedGames = ref<string[]>([]);
const { getRelatedGamesSync } = useRelatedGames();
const { findMatchingBet, createRelatedBet } = useRelatedGameMatching();

const hasRelatedGamesForLottery = computed(() => {
  if (!lottery.value) return false;
  const related = getRelatedGamesSync(lottery.value.slug);
  return Array.isArray(related) && related.length > 0;
});

const isStreamableGame = computed(() =>
  lottery.value?.slug
    ? canShowLiveStream(lottery.value.slug, gameStore.getBetSettings?.enabled_modules)
    : false
);

const countdown = ref({
  hours: 0,
  minutes: 0,
  seconds: 0
});

const updateCountdown = () => {
  const validDraws = gameStore.getValidDraws;
  if (!validDraws || validDraws.length === 0) {
    countdown.value = { hours: 0, minutes: 0, seconds: 0 };
    return;
  }

  const earliestDraw = validDraws.reduce((earliest, draw) => {
    const cutoffTime = new Date(draw.cutoff || draw.draw_date);
    const earliestTime = new Date(earliest.cutoff || earliest.draw_date);
    return cutoffTime < earliestTime ? draw : earliest;
  }, validDraws[0]);
  
  try {
    const now = new Date();
    const cutoffRaw = earliestDraw.cutoff || earliestDraw.draw_date;
    
    let cutoffTime: Date;
    if (typeof cutoffRaw === 'string') {
      const dateStr = cutoffRaw.includes('T') ? cutoffRaw : cutoffRaw.replace(' ', 'T');
      cutoffTime = new Date(dateStr);
    } else {
      cutoffTime = new Date(cutoffRaw);
    }
    
    if (isNaN(cutoffTime.getTime())) {
      countdown.value = { hours: 0, minutes: 0, seconds: 0 };
      return;
    }
    
    const diff = cutoffTime.getTime() - now.getTime();

    if (diff <= 0) {
      countdown.value = { hours: 0, minutes: 0, seconds: 0 };
      return;
    }

    countdown.value = {
      hours: Math.floor(diff / (1000 * 60 * 60)),
      minutes: Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60)),
      seconds: Math.floor((diff % (1000 * 60)) / 1000)
    };
  } catch (error) {
    console.error('Error updating countdown:', error);
    countdown.value = { hours: 0, minutes: 0, seconds: 0 };
  }
};

let countdownTimer: number;

const betslipStore = useBetslipStore();
const { addBet, removeBet: removeBetFromSlip, clearBetslip } = betslipStore;
const betslip = computed(() => betslipStore.betslip);

watch(
  () => route.params.slug,
  async (newSlug) => {
    if (newSlug) {
      try {
        await gameStore.fetchGameDetails(newSlug as string)
        const gameDetail = gameStore.getGameDetail;
        if (gameDetail) {
          lottery.value = {
            id: gameDetail.id.toString(),
            name: gameDetail.name,
            slug: gameDetail.slug,
            flagUrl: gameDetail.logo,
            drawTime: new Date(gameDetail.current_draw.draw_date),
            cutoffTime: new Date(gameDetail.current_draw.cutoff),
            country: gameDetail.game_type.name,
            draw_date: gameDetail.current_draw.draw_date,
            cutoff: gameDetail.current_draw.cutoff,
            game_name: gameDetail.name,
            game_slug: gameDetail.slug,
            game_logo: gameDetail.logo
          };

          applyDrawSelectionFromQuery();

          syncBettingMarketsFromStore();
        }
      } catch (error) {
        console.error('Error fetching game details:', error);
        router.push({ path: '/all', query: { ...route.query } });
      }
    }
  },
  { immediate: true }
);

watch(
  () => route.query,
  async () => {
    if (route.params.slug) {
      await gameStore.fetchGameDetails(route.params.slug as string);
      syncBettingMarketsFromStore();
    }
  },
  { deep: true }
);

watch(
  () => gameStore.gameDetails,
  () => {
    if (gameStore.gameDetails) {
      syncBettingMarketsFromStore();
    }
  }
);

watch(
  () => authStore.account || sessionStorage.getItem('auth_account'),
  async (account, previousAccount) => {
    if (!account || account === previousAccount || !route.params.slug) {
      return;
    }

    try {
      await gameStore.fetchGameDetails(route.params.slug as string, false);
      syncBettingMarketsFromStore();
    } catch (error) {
      console.error('Failed to refresh game details for account:', error);
    }
  }
);

watch(
  () => selectedDrawsArray.value,
  (newSelection) => {
    const selectedDraws = gameStore.getValidDraws.filter((_, index) => newSelection[index]);
    emit('drawsChanged', selectedDraws);
  },
  { deep: true }
);

watch(
  () => lottery.value,
  (newLottery) => {
    if (countdownTimer) {
      clearInterval(countdownTimer);
    }
    
    if (newLottery?.cutoff) {
      updateCountdown();
      countdownTimer = window.setInterval(updateCountdown, 1000);
    }
  },
  { immediate: true }
);

watch(
  () => gameStore.getValidDraws,
  (newDraws) => {
    if (countdownTimer) {
      clearInterval(countdownTimer);
    }

    if (route.query.draw) {
      applyDrawSelectionFromQuery();
    }
    
    if (newDraws && newDraws.length > 0) {
      updateCountdown();
      countdownTimer = window.setInterval(updateCountdown, 1000);
    }
  },
  { immediate: true }
);

watch(
  () => route.query.draw,
  () => {
    applyDrawSelectionFromQuery();
  }
);


const openBetslip = () => {
  if (!useOnlinePlatformBetslip.value) {
    showToast({
      title: t('toast.externalBetslip'),
      message: t('toast.externalBetslipMsg'),
      type: 'info',
      duration: 4000,
    });
    return;
  }
  modalStore.openBetslip();
};

const goBackToLotteries = () => {
  router.push({
    name: 'all',
    query: { ...route.query },
  });
};

const clearAllLines = () => {
  lines.value = [];
  selectedRelatedGames.value = [];
};

const handleClearedAfterSubmission = () => {
  isClearingAfterSubmission.value = true;
  lines.value = [];
  mobileActiveTab.value = 'main';
  
  applyDrawSelectionFromQuery();
};

const selectDraws = (drawCount: number) => {
  const availableDraws = gameStore.getValidDraws || [];
  const maxDraws = Math.min(availableDraws.length, drawCount);
  
  selectedDrawsArray.value = new Array(availableDraws.length).fill(false);
  
  for (let i = 0; i < maxDraws; i++) {
    selectedDrawsArray.value[i] = true;
  }
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const handleSelectDraws = (_drawData: {
  betIndex: number;
  drawCount: number;
  selectedDraws: Array<{draw_date: string, cutoff: string, event_id: number}>;
  gameId: string | number;
}) => {
  // Disabled: draw selection UI should be independent from betslip edits
  return;
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const handleDrawsChangedFromBetSlip = (_selectedDraws: Array<{draw_date: string, cutoff: string, event_id: number}>) => {
  // Only reset to first draw if betslip is empty
  if (betslip.value.length === 0 && !isClearingAfterSubmission.value) {
    const availableDraws = gameStore.getValidDraws || [];
    if (availableDraws.length > 0 && !selectedDrawsArray.value.some(Boolean)) {
      selectedDrawsArray.value = new Array(availableDraws.length).fill(false);
      selectedDrawsArray.value[0] = true;
    }
  }
};

const toggleDrawSelection = (index: number) => {
  selectedDrawsArray.value[index] = !selectedDrawsArray.value[index];
};

const handleUpdateSelectedDraws = (index: number, checked: boolean) => {
  selectedDrawsArray.value[index] = checked;
};

const expandedGroupIndex = ref<number | null>(0);
const toggleGroup = (idx: number) => {
  expandedGroupIndex.value = expandedGroupIndex.value === idx ? null : idx;
};

const onMobileTabChanged = (tab: 'main' | 'bonus') => {
  mobileActiveTab.value = tab;
};

const selectMarket = async (bet: {
  marketGroup: MarketGroup;
  market: Market;
  label: string;
  odds: number;
  select_numbers: boolean;
  balls: number;
  outcome_id: string;
  specifier?: string;
  game_market_id?: number;
}) => {
  if (!bet) return;
  
  if (bet.select_numbers) {
    selectedMarket.value = bet;
    if (isMobile.value) {
      mobileActiveTab.value = 'main';
      onMobileTabChanged('main');
    }
  } else {
    selectedMarket.value = null;
    const selectedDrawDates = gameStore.getValidDraws.filter((_, index) => selectedDrawsArray.value[index]);
    
    const outcome = resolveMarketOutcome(
      bet.market.outcomes,
      bet.outcome_id,
      bet.specifier,
      bet.game_market_id
    );

    const specifier =
      bet.specifier ??
      (outcome?.specifier != null ? normalizeSpecifier(outcome.specifier) : null) ??
      null;

    let betType = bet.label;
    let marketDescription = bet.market.description;
    if (outcome?.specifier && bet.label.includes('{value}')) {
      betType = bet.label.replace('{value}', outcome.specifier);
      marketDescription = bet.market.description.replace('{value}', outcome.specifier);
    }
    
    const betslipItem: BetslipItem = {
      market_id: bet.market.id,
      market_group_id: bet.marketGroup.id,
      game_id: gameStore.getGameDetail?.id || 0,
      game_name: gameStore.getGameDetail?.name,
      draws: selectedDrawDates,
      allAvailableDraws: gameStore.getValidDraws || [],
      drawCount: selectedDrawDates.length,
      numbers: [],
      stake: Number(gameStore.getBetSettings?.bet_default) ?? 1,
      betType: betType,
      maxStake: Number(gameStore.getBetSettings?.bet_max) ?? 10000,
      odds: bet.odds,
      numBets: 1,
      picks: bet.balls,
      outcome_id: bet.outcome_id,
      label: bet.label,
      market_name: bet.market.name,
      market_description: marketDescription,
      specifier: specifier || null,
      game_market_id: bet.game_market_id ?? outcome?.game_market_id ?? undefined,
      currency_symbol: gameStore.getBetSettings?.currency_symbol,
    };

    if (!useOnlinePlatformBetslip.value) {
      iframeCommunication.sendMessage('bets-added', { bets: [betslipItem] });
      try {
        window.dispatchEvent(
          new CustomEvent('numbers-online-betslip-selection', { detail: { bets: [betslipItem] } })
        );
      } catch {
        /* ignore */
      }
      showToast({
        title: t('toast.selectionSent'),
        message: t('toast.externalIntegrationMsg'),
        type: 'info',
        duration: 4000,
      });
      selectedRelatedGames.value = [];
      modalStore.closeBetslip();
      return;
    }

    let duplicateBets = 0;

    // Twin/sum markets: one outcome per market (Odd replaces Even, not both in slip).
    const twinSiblingIndex = betslip.value.findIndex(
      (item) =>
        item.game_id === betslipItem.game_id &&
        item.market_id === betslipItem.market_id &&
        item.numbers.length === 0 &&
        String(item.outcome_id) !== String(betslipItem.outcome_id)
    );
    if (twinSiblingIndex !== -1) {
      removeBetFromSlip(twinSiblingIndex);
    }

    const betAdded = addBet(betslipItem);
    
    if (!betAdded) {
      duplicateBets++;
      showToast({
        title: t('toast.betAlreadyAdded'),
        message: t('toast.betAlreadyAddedMsg'),
        type: 'info',
        duration: 3000
      });
    } else {
      lastAddedBet.value = betslipItem;
      const allAddedBets: BetslipItem[] = [betslipItem];

      if (selectedRelatedGames.value.length > 0 && lottery.value && hasRelatedGamesForLottery.value) {
        const originalGameSlug = lottery.value.slug;
        const relatedGames = getRelatedGamesSync(lottery.value.slug);
        const selectedGames = relatedGames.filter(g => selectedRelatedGames.value.includes(g.slug));

        for (const relatedGame of selectedGames) {
          try {
            const matchingResult = await findMatchingBet(relatedGame, betslipItem);
            const relatedBet = createRelatedBet(matchingResult, betslipItem);
            const added = addBet(relatedBet);
            if (!added) {
              duplicateBets++;
            } else {
              allAddedBets.push(relatedBet);
            }
          } catch (error) {
            console.error(`Error adding bet to ${relatedGame.name}:`, error);
          }
        }
        
        // Restore original game
        await gameStore.fetchGameDetails(originalGameSlug, false);
      }

      // Send all added bets upstream
      iframeCommunication.sendMessage('bets-added', { 
        bets: allAddedBets 
      });

      if (duplicateBets > 0) {
        showToast({
          title: t('toast.duplicateBets'),
          message: t('toast.duplicateBetsSkipped', { count: duplicateBets }),
          type: 'info',
          duration: 3000
        });
      }

      selectedRelatedGames.value = [];

      modalStore.closeBetslip();
    }
  }
};

const removeMarket = (bet: {
  marketGroup: MarketGroup;
  market: Market;
  label: string;
  odds: number;
  select_numbers: boolean;
  balls: number;
  outcome_id: string;
}) => {
  const betToRemove = betslip.value.find(item => 
    item.outcome_id === bet.outcome_id &&
    item.market_id === bet.market.id
  );
  
  if (betToRemove) {
    removeBetFromSlip(betslip.value.indexOf(betToRemove));
    showToast({
      title: t('toast.betRemoved'),
      message: t('toast.betRemovedMsg', { label: bet.label }),
      type: 'info',
      duration: 2000
    });
  }
};

const addLine = (line: Line) => {
  if (!line || !selectedMarket.value) {
    console.error("No market selected when adding line or line is invalid");
    return;
  }
  
  const lineWithMarket = {
    ...line,
    market: selectedMarket.value
  };
  lines.value.push(lineWithMarket);
};

const removeLine = (index: number) => {
  lines.value.splice(index, 1);
};

const isPlacingBet = ref(false);

const addToBetslip = async (mobileLines?: Line[]) => {
  if (isPlacingBet.value) return;
  
  const linesToProcess = mobileLines || lines.value;
  
  if (linesToProcess.length === 0) {
    console.error("No lines to add");
    return;
  }

  const hasEmbeddedMarket = linesToProcess.some((line) => line.market);
  if (selectedMarket.value === null && !hasEmbeddedMarket) {
    console.error("No market or lines selected");
    return;
  }

  if (!selectedDrawsArray.value.some((selected) => selected)) {
    if (gameStore.getValidDraws.length > 0) {
      selectedDrawsArray.value[0] = true;
    } else {
      console.error("No draws available");
      return;
    }
  }

  const selectedDrawDates = gameStore.getValidDraws.filter((_, index) => selectedDrawsArray.value[index]);
  if (!selectedDrawDates.length) {
    console.error("No draw date selected");
    return;
  }

  let duplicateBets = 0;
  const successfullyAddedBets: BetslipItem[] = [];
  let maxBetsReached = false;
  
  for (const line of linesToProcess) {
    if (maxBetsReached) {
      break;
    }

    const lineMarket = line.market || selectedMarket.value;
    if (!lineMarket) {
      console.error("No market information found for line");
      continue;
    }
    
    const isBonusOnly = lineMarket.marketGroup.slug === 'bonus-ball-only-additional-drum' || 
                       lineMarket.marketGroup.slug === 'bonus-ball-only-same-drum';
    const isMainIncludingBonus = lineMarket.marketGroup.slug === 'standard-bet-bonus-from-same-drum';
    const isColorCombo = isStandardBetPlusColorBonusMarket(lineMarket.market?.slug ?? '');
    const numbersPayload = isColorCombo
      ? [Array.isArray(line.numbers) ? line.numbers : [], []]
      : [
          isBonusOnly ? [] : (Array.isArray(line.numbers) ? line.numbers : []),
          (!isMainIncludingBonus && line.bonus !== undefined && line.bonus !== null) ? [line.bonus] : []
        ];
    const bet = {
      market_id: lineMarket.market.id,
      market_group_id: lineMarket.marketGroup.id,
      game_name: gameStore.getGameDetail?.name,
      game_id: gameStore.getGameDetail?.id || 0,
      numbers: numbersPayload,
      bonus: line.bonus,
      stake: Number(gameStore.getBetSettings?.bet_default) ?? 1,
      draws: selectedDrawDates,
      allAvailableDraws: gameStore.getValidDraws || [],
      drawCount: selectedDrawDates.length,
      betType: lineMarket.label,
      maxStake: Number(gameStore.getBetSettings?.bet_max) ?? 10000,
        odds: lineMarket.odds,
        picks: isBonusOnly ? 1 : (line.picks || lineMarket.balls),
        numBets: 1,
        label: lineMarket.label,
        market_name: lineMarket.market.name,
        market_description: lineMarket.market.description,
        outcome_id: lineMarket.outcome_id,
        game_market_id: lineMarket.game_market_id ?? undefined,
      currency_symbol: gameStore.getBetSettings?.currency_symbol,
    };

    const betAdded = useOnlinePlatformBetslip.value ? addBet(bet) : true;
    if (!betAdded) {
      duplicateBets++;
    } else {
      successfullyAddedBets.push(bet);
      lastAddedBet.value = bet;
    }
  }

  // Process related games
  if (selectedRelatedGames.value.length > 0 && successfullyAddedBets.length > 0 && lottery.value) {
    const originalGameSlug = lottery.value.slug;
    const relatedGames = getRelatedGamesSync(lottery.value.slug);
    const selectedGames = relatedGames.filter(g => selectedRelatedGames.value.includes(g.slug));
    const relatedBetsToAdd: BetslipItem[] = [];

    for (const addedBet of successfullyAddedBets) {
      for (const relatedGame of selectedGames) {
        try {
            const matchingResult = await findMatchingBet(relatedGame, addedBet);
            const relatedBet = createRelatedBet(matchingResult, addedBet);
            const added = useOnlinePlatformBetslip.value ? addBet(relatedBet) : true;
            if (!added) {
              duplicateBets++;
            } else {
              relatedBetsToAdd.push(relatedBet);
            }
        } catch (error) {
          console.error(`Error adding bet to ${relatedGame.name}:`, error);
        }
      }
    }
    successfullyAddedBets.push(...relatedBetsToAdd);
    
    // Restore original game
    await gameStore.fetchGameDetails(originalGameSlug, false);
  }

  // Send data upstream for all successfully added bets (including related games)
  if (successfullyAddedBets.length > 0) {
    iframeCommunication.sendMessage('bets-added', { 
      bets: successfullyAddedBets 
    });
    if (!useOnlinePlatformBetslip.value) {
      try {
        window.dispatchEvent(
          new CustomEvent('numbers-online-betslip-selection', { detail: { bets: successfullyAddedBets } })
        );
      } catch {
        /* ignore */
      }
      showToast({
        title: t('toast.selectionSent'),
        message: t('toast.externalIntegrationMsg'),
        type: 'info',
        duration: 4000,
      });
    }
  }
  
  if (duplicateBets > 0) {
    showToast({
      title: t('toast.duplicateBets'),
      message: t('toast.duplicateBetsSkipped', { count: duplicateBets }),
      type: 'info',
      duration: 3000
    });
  }

  lines.value = [];
  selectedRelatedGames.value = [];
  mobileActiveTab.value = 'main';
  
};


let drawsRefreshTimer: number | null = null;

const setupDrawRefreshTimer = () => {
  const validDraws = gameStore.getValidDraws;
  if (validDraws.length === 0) return;

  const earliestCutoff = validDraws.reduce((earliest, draw) => {
    const cutoffTime = new Date(draw.cutoff || draw.draw_date);
    return cutoffTime < earliest ? cutoffTime : earliest;
  }, new Date(validDraws[0].cutoff || validDraws[0].draw_date));

  const now = new Date();
  const timeUntilExpiry = earliestCutoff.getTime() - now.getTime();

  if (timeUntilExpiry > 0) {
    if (drawsRefreshTimer) {
      clearTimeout(drawsRefreshTimer);
    }

    drawsRefreshTimer = window.setTimeout(() => {
      gameStore.refreshDraws().then(() => {
        if (countdownTimer) {
          clearInterval(countdownTimer);
        }
        updateCountdown();
        countdownTimer = window.setInterval(updateCountdown, 1000);
        setupDrawRefreshTimer();
      });
    }, timeUntilExpiry + 1000);
  }
};

onMounted(async () => {
  window.addEventListener("resize", handleResize);
  syncMobileViewportLayout();
  window.scrollTo({
    top: 0,
    behavior: 'smooth'
  });
  setupDrawRefreshTimer();
});

watch([isMobile, useOnlinePlatformBetslip], syncMobileViewportLayout);

watch(() => route.path, () => {
  window.scrollTo({
    top: 0,
    behavior: 'smooth'
  });
});

watch(() => gameStore.getValidDraws, (newDraws, oldDraws) => {
  if (newDraws.length !== oldDraws?.length) {
    const newSelection = new Array(newDraws.length).fill(false);
    
    if (oldDraws && oldDraws.length > 0) {
      oldDraws.forEach((oldDraw: { event_id: string | number }, oldIndex: number) => {
        if (selectedDrawsArray.value[oldIndex]) {
          const matchingIndex = newDraws.findIndex((newDraw: { event_id: string | number }) => 
            newDraw.event_id === oldDraw.event_id
          );
          if (matchingIndex !== -1) {
            newSelection[matchingIndex] = true;
          }
        }
      });
    }
    
    if (!newSelection.some(Boolean) && newDraws.length > 0) {
      newSelection[0] = true;
    }
    
    selectedDrawsArray.value = newSelection;
    setupDrawRefreshTimer();
  }
}, { immediate: true });

watch(() => betslip.value.length, (newLength, oldLength) => {
  if (oldLength && oldLength > 0 && newLength === 0) {
    if (!isClearingAfterSubmission.value) {
      selectedMarket.value = null;
      mobileActiveTab.value = 'main';
    }
    isClearingAfterSubmission.value = false;
  }
});

watch(() => modalStore.showBetslip, (isOpen) => {
  if (!isOpen && isMobile.value) {
    mobileActiveTab.value = 'main';
    lines.value = [];
  }
});

let resizeTimeout: number | null = null;
const handleResize = () => {
  if (resizeTimeout) {
    clearTimeout(resizeTimeout);
  }
  resizeTimeout = window.setTimeout(() => {
    const newIsMobile = window.innerWidth <= 768;
    if (newIsMobile !== isMobile.value) {
      selectedMarket.value = null;
      lines.value = [];
      mobileActiveTab.value = 'main';
    }
    isMobile.value = newIsMobile;
    syncMobileViewportLayout();
  }, 100);
};

onUnmounted(() => {
  document.body.classList.remove(DETAIL_MOBILE_VIEWPORT_CLASS);
  document.body.classList.remove(DETAIL_STICKY_HEADER_CLASS);
  document.documentElement.style.removeProperty('--detail-mobile-viewport-height');
  document.documentElement.style.removeProperty('--detail-mobile-fixed-footer-height');
  if (iframeCommunication.isRunningInIframe()) {
    iframeCommunication.sendImmediateHeightUpdate();
  }
  window.removeEventListener("resize", handleResize);
  if (resizeTimeout) {
    clearTimeout(resizeTimeout);
  }
  if (countdownTimer) {
    clearInterval(countdownTimer);
  }
  if (drawsRefreshTimer) {
    clearTimeout(drawsRefreshTimer);
  }
});


__expose({
  selectDraws
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    class: _normalizeClass(["detail-view", { 'detail-view--mobile': isMobile.value }])
  }, [
    (isMobile.value)
      ? (_openBlock(), _createBlock(MobileHeader, {
          key: 0,
          lottery: lottery.value,
          countdown: countdown.value,
          "show-watch-live": isStreamableGame.value,
          onGoBack: goBackToLotteries,
          onWatchLive: _cache[0] || (_cache[0] = ($event: any) => (showStream.value = true))
        }, null, 8, ["lottery", "countdown", "show-watch-live"]))
      : _createCommentVNode("", true),
    _createElementVNode("div", _hoisted_1, [
      _createElementVNode("div", _hoisted_2, [
        (!isMobile.value && lottery.value)
          ? (_openBlock(), _createBlock(DesktopHeader, {
              key: 0,
              lottery: lottery.value,
              countdown: countdown.value,
              "show-watch-live": isStreamableGame.value,
              onGoBack: goBackToLotteries,
              onWatchLive: _cache[1] || (_cache[1] = ($event: any) => (showStream.value = true))
            }, null, 8, ["lottery", "countdown", "show-watch-live"]))
          : _createCommentVNode("", true),
        (isMobile.value)
          ? (_openBlock(), _createBlock(MobileDrawsSection, {
              key: 1,
              draws: _unref(gameStore).getValidDraws,
              "selected-draws": selectedDrawsArray.value,
              onUpdateSelectedDraws: handleUpdateSelectedDraws,
              onToggleDrawSelection: toggleDrawSelection
            }, null, 8, ["draws", "selected-draws"]))
          : (_openBlock(), _createBlock(DesktopDrawsSection, {
              key: 2,
              draws: _unref(gameStore).getValidDraws,
              "selected-draws": selectedDrawsArray.value,
              onUpdateSelectedDraws: handleUpdateSelectedDraws,
              onToggleDrawSelection: toggleDrawSelection
            }, null, 8, ["draws", "selected-draws"])),
        (lottery.value && hasRelatedGamesForLottery.value && !_unref(isLite))
          ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
              _createElementVNode("div", _hoisted_4, [
                _createElementVNode("span", _hoisted_5, _toDisplayString(_ctx.$t('detail.alsoAddTo')), 1)
              ]),
              _createVNode(RelatedGamesSelector, {
                "current-game-slug": lottery.value.slug,
                "selected-games": selectedRelatedGames.value,
                "is-processing": isPlacingBet.value,
                "onUpdate:selectedGames": _cache[2] || (_cache[2] = ($event: any) => (selectedRelatedGames.value = $event))
              }, null, 8, ["current-game-slug", "selected-games", "is-processing"])
            ]))
          : _createCommentVNode("", true),
        (isMobile.value)
          ? (_openBlock(), _createBlock(MobileBettingSection, {
              key: `mobile-betting-${isMobile.value}`,
              "betting-markets": bettingMarkets.value,
              "selected-market": selectedMarket.value,
              "hot-numbers": _unref(gameStore).getHotNumbers,
              "cold-numbers": _unref(gameStore).getColdNumbers,
              "betslip-length": betslip.value.length,
              "streamlined-selection": streamlinedNumberSelection.value,
              "enable-hot-cold-numbers": hotColdNumbersEnabled.value,
              onSelectMarket: selectMarket,
              onRemoveMarket: removeMarket,
              onToggleGroup: toggleGroup,
              onTabChanged: onMobileTabChanged,
              onAddLine: addLine,
              onRemoveLine: removeLine,
              onAddToBetslip: _cache[3] || (_cache[3] = (mobileLines) => addToBetslip(mobileLines)),
              onClearAll: clearAllLines
            }, null, 8, ["betting-markets", "selected-market", "hot-numbers", "cold-numbers", "betslip-length", "streamlined-selection", "enable-hot-cold-numbers"]))
          : _createCommentVNode("", true),
        (!isMobile.value)
          ? (_openBlock(), _createBlock(DesktopBettingSection, {
              key: `desktop-betting-${!isMobile.value}`,
              "betting-markets": bettingMarkets.value,
              "selected-market": selectedMarket.value,
              "hot-numbers": _unref(gameStore).getHotNumbers,
              "cold-numbers": _unref(gameStore).getColdNumbers,
              "betslip-length": betslip.value.length,
              "streamlined-selection": streamlinedNumberSelection.value,
              "enable-hot-cold-numbers": hotColdNumbersEnabled.value,
              onSelectMarket: selectMarket,
              onRemoveMarket: removeMarket,
              onToggleGroup: toggleGroup,
              onTabChanged: onMobileTabChanged,
              onAddLine: addLine,
              onRemoveLine: removeLine,
              onAddToBetslip: _cache[4] || (_cache[4] = (desktopLines) => addToBetslip(desktopLines)),
              onClearAll: clearAllLines
            }, null, 8, ["betting-markets", "selected-market", "hot-numbers", "cold-numbers", "betslip-length", "streamlined-selection", "enable-hot-cold-numbers"]))
          : _createCommentVNode("", true),
        (isMobile.value && selectedMarket.value && (selectedMarket.value.select_numbers || _unref(isStandardBetPlusColorBonusMarket)(selectedMarket.value.market?.slug ?? '')) && mobileActiveTab.value === 'main' && !streamlinedNumberSelection.value)
          ? (_openBlock(), _createBlock(MobileConfirmSection, {
              key: `mobile-confirm-${isMobile.value}`,
              lines: lines.value,
              "current-game-slug": lottery.value?.slug,
              "selected-related-games": selectedRelatedGames.value,
              "is-processing": isPlacingBet.value,
              onClearAll: clearAllLines,
              onRemoveLine: removeLine,
              onAddToBetslip: addToBetslip,
              "onUpdate:selectedRelatedGames": _cache[5] || (_cache[5] = ($event: any) => (selectedRelatedGames.value = $event))
            }, null, 8, ["lines", "current-game-slug", "selected-related-games", "is-processing"]))
          : _createCommentVNode("", true),
        (!isMobile.value && selectedMarket.value && (selectedMarket.value.select_numbers || _unref(isStandardBetPlusColorBonusMarket)(selectedMarket.value.market?.slug ?? '')) && !streamlinedNumberSelection.value)
          ? (_openBlock(), _createBlock(DesktopConfirmSection, {
              key: `desktop-confirm-${!isMobile.value}`,
              lines: lines.value,
              "current-game-slug": lottery.value?.slug,
              "selected-related-games": selectedRelatedGames.value,
              "is-processing": isPlacingBet.value,
              onClearAll: clearAllLines,
              onRemoveLine: removeLine,
              onAddToBetslip: addToBetslip,
              "onUpdate:selectedRelatedGames": _cache[6] || (_cache[6] = ($event: any) => (selectedRelatedGames.value = $event))
            }, null, 8, ["lines", "current-game-slug", "selected-related-games", "is-processing"]))
          : _createCommentVNode("", true),
        (isMobile.value)
          ? (_openBlock(), _createElementBlock("div", _hoisted_6, [
              _createElementVNode("button", {
                class: "mobile-back-button",
                onClick: goBackToLotteries
              }, [
                _cache[8] || (_cache[8] = _createElementVNode("svg", {
                  class: "mobile-back-icon",
                  viewBox: "0 0 24 24",
                  fill: "none",
                  stroke: "currentColor",
                  "stroke-width": "2"
                }, [
                  _createElementVNode("path", { d: "M19 12H5M12 19l-7-7 7-7" })
                ], -1)),
                _createElementVNode("span", _hoisted_7, _toDisplayString(_ctx.$t('detail.backToLotteries')), 1)
              ])
            ]))
          : _createCommentVNode("", true)
      ]),
      (useOnlinePlatformBetslip.value)
        ? (_openBlock(), _createElementBlock("div", _hoisted_8, [
            _createVNode(BetSlip, {
              onRemoveBet: _unref(removeBetFromSlip),
              onClear: _unref(clearBetslip),
              onClearedAfterSubmission: handleClearedAfterSubmission,
              onSelectDraws: handleSelectDraws,
              onDrawsChanged: handleDrawsChangedFromBetSlip
            }, null, 8, ["onRemoveBet", "onClear"])
          ]))
        : _createCommentVNode("", true)
    ]),
    (_openBlock(), _createBlock(_Teleport, { to: "body" }, [
      (isMobile.value && useOnlinePlatformBetslip.value)
        ? (_openBlock(), _createElementBlock("div", _hoisted_9, [
            _createElementVNode("button", {
              type: "button",
              class: "mobile-view-bets",
              onClick: openBetslip
            }, _toDisplayString(_ctx.$t('detail.viewBets')) + " (" + _toDisplayString(betslip.value.length) + ") ", 1)
          ]))
        : _createCommentVNode("", true)
    ])),
    (showStream.value && lottery.value)
      ? (_openBlock(), _createBlock(VideoLiveStream, {
          key: 1,
          "game-slug": lottery.value.slug,
          onClose: _cache[7] || (_cache[7] = ($event: any) => (showStream.value = false))
        }, null, 8, ["game-slug"]))
      : _createCommentVNode("", true)
  ], 2))
}
}

})